import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Waves, Clock, MessageCircle, ExternalLink } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PhotoManager } from "@/components/admin/PhotoManager";
import { mediaUrl } from "@/lib/pricing";
import { Panel, BRL, inputCls, labelCls, btnPrimary, btnGhost, iconBtn } from "./ui";

const EMPTY = {
  title: "", location: "Guarujá, SP", description: "", price: "", price_unit: "por jet ski", duration_min: 30,
  meeting_point: "", whatsapp: "", images: [], time_slots: "09:00, 11:00, 14:00, 16:00", units: 2,
  max_people_per_unit: 2, deposit_percent: 50,
};
const swCls = "data-[state=checked]:bg-[#0A66C2] data-[state=unchecked]:bg-slate-200 [&>span]:bg-white";

const Input = ({ label, k, f, set, type = "text", ph, span = "" }) => (
  <div className={span}>
    <label className={labelCls}>{label}</label>
    <input type={type} value={f[k]} placeholder={ph} onChange={(e) => set({ [k]: e.target.value })} className={inputCls} data-testid={`tour-${k}`} />
  </div>
);

const TourForm = ({ open, onOpenChange, initial, onSave }) => {
  const [f, setF] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const set = (p) => setF((x) => ({ ...x, ...p }));

  useEffect(() => {
    setF(initial ? { ...EMPTY, ...initial, time_slots: (initial.time_slots || []).join(", ") } : EMPTY);
  }, [initial, open]);

  const submit = async () => {
    if (!f.title.trim() || !(Number(f.price) > 0)) return toast.error("Preencha o nome e o preço do passeio.");
    setSaving(true);
    try {
      const ok = await onSave({
        title: f.title.trim(), location: f.location.trim(), description: f.description.trim(), price: Number(f.price),
        price_unit: f.price_unit.trim() || "por jet ski", duration_min: Number(f.duration_min), meeting_point: f.meeting_point.trim(),
        whatsapp: f.whatsapp.trim(), images: f.images, units: Number(f.units), max_people_per_unit: Number(f.max_people_per_unit),
        deposit_percent: Number(f.deposit_percent), time_slots: f.time_slots.split(/[,;\s]+/).filter(Boolean),
      });
      if (ok !== false) onOpenChange(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white text-slate-900 border-[#EDE6DA] rounded-3xl max-w-2xl max-h-[92vh] overflow-y-auto" style={{ fontFamily: "Outfit, sans-serif" }} data-testid="tour-form-dialog">
        <DialogHeader>
          <DialogTitle className="text-2xl text-slate-900" style={{ fontFamily: "Fraunces, serif" }}>{initial ? "Editar passeio" : "Novo passeio de jet ski"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <Input label="Nome do passeio" k="title" f={f} set={set} ph="Jet Ski na Enseada — Guarujá" />
          <div className="grid sm:grid-cols-2 gap-3">
            <Input label="Local" k="location" f={f} set={set} ph="Guarujá, SP" />
            <Input label="WhatsApp do passeio" k="whatsapp" f={f} set={set} ph="(13) 99999-9999" />
          </div>
          <div>
            <label className={labelCls}>Descrição</label>
            <textarea value={f.description} onChange={(e) => set({ description: e.target.value })} className={`${inputCls} h-24 py-2`} data-testid="tour-description" />
          </div>
          <Input label="Ponto de encontro" k="meeting_point" f={f} set={set} ph="Praia da Enseada, em frente ao quiosque X" />
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Input label="Preço (R$)" k="price" f={f} set={set} type="number" />
            <Input label="Cobrado" k="price_unit" f={f} set={set} ph="por jet ski" />
            <Input label="Duração (min)" k="duration_min" f={f} set={set} type="number" />
            <Input label="Sinal (%, mín. 1)" k="deposit_percent" f={f} set={set} type="number" />
            <Input label="Jet skis por horário" k="units" f={f} set={set} type="number" />
            <Input label="Pessoas por jet ski" k="max_people_per_unit" f={f} set={set} type="number" />
            <Input label="Horários (separe por vírgula)" k="time_slots" f={f} set={set} ph="09:00, 11:00, 14:00" span="col-span-2" />
          </div>
          <PhotoManager images={f.images} onChange={(images) => set({ images })} />
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={() => onOpenChange(false)} className={btnGhost} data-testid="tour-cancel">Cancelar</button>
            <button onClick={submit} disabled={saving} className={btnPrimary} data-testid="tour-save">{saving ? "Salvando..." : "Salvar passeio"}</button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const ToursAdmin = ({ items, onAdd, onUpdate, onDelete }) => {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const save = (payload) => (editing ? onUpdate(editing.id, payload) : onAdd(payload));
  const list = items.filter((t) => !t.deleted);

  return (
    <Panel id="passeios" title="Passeios de Jet Ski" subtitle="Guarujá e litoral de SP: fotos, WhatsApp, horários e preços." testId="tours-admin"
      action={<button onClick={() => { setEditing(null); setOpen(true); }} className={btnPrimary} data-testid="add-tour-btn"><Plus size={15} /> Novo passeio</button>}>
      <TourForm open={open} onOpenChange={setOpen} initial={editing} onSave={save} />
      {list.length === 0 ? (
        <p className="text-sm text-slate-500 text-center py-10">Nenhum passeio cadastrado.</p>
      ) : (
        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {list.map((t) => (
            <article key={t.id} className={`rounded-3xl border border-slate-200 overflow-hidden ${t.active ? "" : "opacity-60"}`} data-testid={`admin-tour-${t.id}`}>
              <div className="relative h-36 bg-cyan-50">
                {t.images?.[0] ? <img src={mediaUrl(t.images[0])} alt={t.title} className="w-full h-full object-cover" />
                  : <div className="w-full h-full flex items-center justify-center text-cyan-300"><Waves size={32} /></div>}
                <span className="absolute bottom-2 left-2 text-[11px] font-bold bg-white/90 text-slate-700 px-2 py-0.5 rounded-full">{t.images?.length || 0} foto(s)</span>
              </div>
              <div className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <h3 className="font-bold text-slate-900 truncate">{t.title}</h3>
                    <p className="text-xs text-slate-500">{t.location}</p>
                  </div>
                  <Switch className={swCls} checked={t.active} onCheckedChange={(v) => onUpdate(t.id, { active: v })} data-testid={`toggle-tour-${t.id}`} />
                </div>
                <div className="flex flex-wrap gap-3 text-xs text-slate-500 mt-2">
                  <span className="flex items-center gap-1"><Clock size={12} /> {t.time_slots.join(" · ")}</span>
                  <span className={`flex items-center gap-1 ${t.whatsapp ? "text-emerald-700" : "text-amber-700"}`}><MessageCircle size={12} /> {t.whatsapp || "sem WhatsApp"}</span>
                </div>
                <div className="flex items-center justify-between mt-3">
                  <span className="font-extrabold text-[#0A66C2]">{BRL(t.price)}<span className="text-xs text-slate-400 font-medium"> {t.price_unit}</span></span>
                  <div className="flex">
                    <a href={`/passeio/${t.id}`} target="_blank" rel="noreferrer" title="Ver página do passeio" className={`${iconBtn} hover:text-[#0A66C2]`} data-testid={`view-tour-${t.id}`}><ExternalLink size={15} /></a>
                    <button className={`${iconBtn} hover:text-[#0A66C2]`} onClick={() => { setEditing(t); setOpen(true); }} data-testid={`edit-tour-${t.id}`}><Pencil size={15} /></button>
                    <button className={`${iconBtn} hover:text-red-600`} onClick={() => window.confirm(`Remover "${t.title}"?`) && onDelete(t.id)} data-testid={`del-tour-${t.id}`}><Trash2 size={15} /></button>
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}
    </Panel>
  );
};
